import { getBetaEmails } from "./features";

/**
 * App Store / production surface control.
 * When true, signed-in users only see meal planning (plan, recipes, calendar,
 * lists, automation, profile). Other hubs and apps stay hidden until ready.
 */
export const MEALS_ONLY_PRODUCT = true;

/** Owner/beta emails can open extra product routes while meals-only stays on for everyone else. */
export function isProductPreviewUser(email) {
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return false;
  return getBetaEmails().includes(normalized);
}

/** Extra paths unlocked for product-preview users (Habits/Chores + other products). */
export const PRODUCT_PREVIEW_PATH_PREFIXES = [
  "/apps",
  "/habits",
  "/chores",
  "/focus",
  "/fitness",
  "/household",
  "/routines",
  "/calories",
  "/budget",
  "/music",
  "/transcribe",
  "/composer",
  "/cs2",
  "/selling",
  "/tv/chores",
  "/tv/yoga",
  "/tv/stretch",
  "/tv/meditation",
  "/tv/workouts",
  "/tv/basketball",
  "/tv/day-trading",
  "/tv/cs2",
];

/** Home hub cards for the meals launch surface. */
export const MEALS_LAUNCH_HUB_IDS = [
  "plan",
  "lists",
  "recipes",
  "calendar",
  "automation",
  "profile",
];

/** Pinned shortcuts shown on those cards. */
export const MEALS_LAUNCH_PINNED_APPS = [
  "plan-meals",
  "lists",
  "recipes",
  "calendar",
  "meal-automation",
];

const MEALS_LAUNCH_HUB_SET = new Set(MEALS_LAUNCH_HUB_IDS);
const MEALS_LAUNCH_PIN_SET = new Set(MEALS_LAUNCH_PINNED_APPS);

export function isMealsLaunchHubLayout(ids) {
  if (!Array.isArray(ids) || ids.length !== MEALS_LAUNCH_HUB_IDS.length) return false;
  return (
    ids.every((id) => MEALS_LAUNCH_HUB_SET.has(id)) &&
    MEALS_LAUNCH_HUB_IDS.every((id) => ids.includes(id))
  );
}

/** Paths new users may open while meals-only is on (prefix match). */
export const MEALS_ALLOWED_PATH_PREFIXES = [
  "/",
  "/workflows/meals",
  "/meal-plans",
  "/automation",
  "/recipes",
  "/calendar",
  "/grocery",
  "/prep-meal",
  "/onboarding",
  "/profile",
  "/subscription",
  "/checkout",
  "/help",
  "/privacy",
  "/terms",
  "/delete-account",
  "/invite",
  "/shared",
  "/sign-in",
  "/sign-up",
  "/tv",
  "/tv/cook",
  "/tv/meals",
  "/tv/recipes",
  "/tv/lists",
];

/** Exact /tv routes that are not meal-related (blocked when meals-only). */
export const MEALS_BLOCKED_TV_PATHS = new Set([
  "/tv/cs2",
  "/tv/chores",
  "/tv/basketball",
  "/tv/yoga",
  "/tv/stretch",
  "/tv/meditation",
  "/tv/day-trading",
  "/tv/workouts",
]);

export function isMealsAllowedPath(pathname) {
  if (!pathname) return false;
  const path = pathname.split("?")[0] || "/";
  if (MEALS_BLOCKED_TV_PATHS.has(path)) return false;
  if (path === "/") return true;
  return MEALS_ALLOWED_PATH_PREFIXES.some(
    (prefix) => prefix !== "/" && (path === prefix || path.startsWith(`${prefix}/`))
  );
}

export function isProductPreviewPath(pathname) {
  if (!pathname) return false;
  const path = pathname.split("?")[0] || "/";
  return PRODUCT_PREVIEW_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

/** Meals allowlist, or product-preview paths when the signed-in user is a beta/owner. */
export function isPathAllowedForUser(pathname, email) {
  if (isMealsAllowedPath(pathname)) return true;
  if (isProductPreviewUser(email) && isProductPreviewPath(pathname)) return true;
  return false;
}

export function filterPinsForProductSurface(pins) {
  if (!MEALS_ONLY_PRODUCT) {
    return Array.isArray(pins) ? pins.filter((id) => typeof id === "string" && id) : [];
  }
  if (!Array.isArray(pins) || pins.length === 0) {
    return [...MEALS_LAUNCH_PINNED_APPS];
  }
  const kept = pins.filter((id) => MEALS_LAUNCH_PIN_SET.has(id));
  return kept.length > 0 ? kept : [...MEALS_LAUNCH_PINNED_APPS];
}

export function pinsNeedMealsLaunchRewrite(pins) {
  if (!MEALS_ONLY_PRODUCT || !Array.isArray(pins)) return false;
  return pins.some((id) => typeof id === "string" && id && !MEALS_LAUNCH_PIN_SET.has(id));
}
